/**
 * 客戶訂單即時刷新（AppSync Events）的純函式與型別。
 *
 * 不 import axios：測試才不會拖進 Sentry／攔截器（同 DeliveryTracking/trackingSession.ts）。
 * 規格：lpg-customer-realtime docs/customer-realtime-spec.md 合約 C3、C6、C7、C8。
 *
 * 為什麼沒有「到期前續發」（配送追蹤有）：
 * AppSync Events 只在 connect / subscribe 握手時呼叫授權 Lambda，連線建立後通行證過期也不會被踢掉；
 * 連線最長 24 小時，斷了就走重連，而每次重連前本來就會重新要一張通行證。
 * 配送追蹤不同——它的 resolver 會在每則推送時檢查 exp，不續發就收不到東西。
 */

/** POST /app-api/customer/realtime-session 的 data（C6；欄位刻意用 camelCase，對齊 tracking-session） */
export type CustomerRealtimeSession = {
  token: string;
  expiresAt: number;
  channel: string;
  realtime: { httpHost: string; wssUrl: string };
};

/** mirror 發布的事件（C8 v1）。只說「該重抓什麼」，不帶訂單內容。 */
export type OrderRealtimeEvent = {
  v: number;
  type: "platform_order_updated";
  platform_order_id: string;
  order_status: string | null;
  sync_version: number | null;
  ts: number | null;
};

/**
 * 斷線重連間隔：3 秒、10 秒，之後固定 30 秒。
 * 比配送追蹤多一段 30 秒：這裡斷線的代價只是晚一點更新（還有 30 秒輪詢保底），
 * 而後端沒部署或密鑰沒建時會一直失敗，不該每 10 秒打一次 app-api。
 */
export const RECONNECT_DELAYS_MS = [3_000, 10_000, 30_000] as const;

export const reconnectDelayMs = (attempt: number): number =>
  RECONNECT_DELAYS_MS[attempt] ?? RECONNECT_DELAYS_MS[RECONNECT_DELAYS_MS.length - 1];

/**
 * 頁面收到事件後多久重抓（trailing debounce）。
 * mirror 每約 10 秒批次一次，同一批可能同時改好幾張單、每張各發一個事件；
 * 1.5 秒內的事件併成一次重抓，不要一張單打一次 API。
 */
export const REALTIME_REFRESH_DEBOUNCE_MS = 1_500;
/** 事件一直來的時候最多拖多久就一定要重抓，避免畫面一直不更新 */
export const REALTIME_REFRESH_MAX_WAIT_MS = 5_000;

/** WebSocket subprotocol 帶授權用的 base64url（C7）：subprotocol 不能有 = + / */
export const base64UrlEncodeJson = (obj: unknown): string =>
  btoa(JSON.stringify(obj)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** 解一次 JSON；解出來還是字串（被雙重編碼）就再解一次。失敗回 undefined。 */
const parseOne = (s: string): unknown => {
  try {
    const once: unknown = JSON.parse(s);
    return typeof once === "string" ? JSON.parse(once) : once;
  } catch {
    return undefined;
  }
};

/**
 * data 訊息的 event 欄位 → 事件物件陣列。
 *
 * AWS 文件的範例裡 event 有時是 JSON 字串、有時是 JSON 字串陣列，兩種都接。
 * 任何一個元素壞掉就略過它，不 throw：一則壞訊息不該弄斷整條連線。
 */
export function parseEventFrame(event: unknown): unknown[] {
  const items: unknown[] = Array.isArray(event) ? event : [event];
  const out: unknown[] = [];
  items.forEach((item) => {
    if (typeof item !== "string") return;
    const parsed = parseOne(item);
    if (parsed !== undefined) out.push(parsed);
  });
  return out;
}

const finiteOrNull = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * 判斷是不是有效的訂單事件（C8）。
 *
 * 只看 type 和 platform_order_id：v 認不得也照收，之後 mirror 加欄位、升版本時前端不用同步改。
 * 其他欄位只是參考資訊（頁面一律重抓 API 拿真資料），型別不對就填 null。
 */
export function parseOrderEvent(x: unknown): OrderRealtimeEvent | null {
  if (!x || typeof x !== "object" || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  if (o.type !== "platform_order_updated") return null;
  if (typeof o.platform_order_id !== "string" || o.platform_order_id.length === 0) return null;
  return {
    v: finiteOrNull(o.v) ?? 1,
    type: "platform_order_updated",
    platform_order_id: o.platform_order_id,
    order_status: typeof o.order_status === "string" ? o.order_status : null,
    sync_version: finiteOrNull(o.sync_version),
    ts: finiteOrNull(o.ts),
  };
}

/** 只有訂單列表與訂單詳情（/orders、/orders/*）才需要即時連線 */
export const isOrdersPath = (pathname: string): boolean => pathname === "/orders" || pathname.startsWith("/orders/");
